import React, { useState, useEffect, useCallback } from "react";
import useApiEp from "../hooks/useApiEp";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { getHeader, toastError } from "../utils/util";
import { LoadingButton } from "@mui/lab";
import { API_ENDPOINT } from "../utils/constants";

const fields = [
  { key: "phoneNumberId", label: "Phone Number ID" },
  { key: "wabaToken", label: "WABA Token" },
  { key: "wabaAccountId", label: "WABA Account ID" },
  { key: "appId", label: "App ID" },
];

const ManageAccounts = () => {
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState({
    phoneNumberId: "",
    wabaToken: "",
    wabaAccountId: "",
    appId: "",
  });

  /* ------------------- fetch lists ------------------- */
  const { data: customerIds = [], isLoading: loadingCustomers } = useApiEp(
    "/api/customer-ids",
    true
  );

  const { data: info = null, isLoading: loadingInfo } = useApiEp(
    `/api/customer-info/${selectedCustomerId}`,
    Boolean(selectedCustomerId)
  );

  /* ------------------- hydrate form ------------------ */
  useEffect(() => {
    if (info) {
      setFormValues((prev) => ({
        ...prev,
        phoneNumberId: info.phoneNumberId ?? "",
        wabaToken: info.wabaToken ?? "",
        wabaAccountId: info.wabaAccountId ?? "",
        appId: info.appId ?? "",
      }));
    }
  }, [info]);

  /* ---------------- handlers ------------------ */
  const handleCustomerSelect = (_, value) => setSelectedCustomerId(value);

  const handleChange = (key) => (e) =>
    setFormValues((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = useCallback(async () => {
    if (!selectedCustomerId) return;
    try {
      setSaving(true);
      const { data } = await axios.put(
        `${API_ENDPOINT}/api/customer-info/${selectedCustomerId}`,
        formValues,
        getHeader()
      );
      toast.success(data.message);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  }, [selectedCustomerId, formValues]);

  return (
    <Container sx={{ mt: 3 }}>
      <Container maxWidth="md">
        <Autocomplete
          options={customerIds}
          loading={loadingCustomers}
          value={selectedCustomerId}
          onChange={handleCustomerSelect}
          size="small"
          sx={{ maxWidth: 320, mb: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Customer ID"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingCustomers && (
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {loadingInfo && <CircularProgress size={24} />}
        {info && (
          <Paper elevation={2} sx={{ p: 3 }}>
            {/* read-only strip */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                bgcolor: "grey.100",
                p: 1.5,
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">
                <strong>Quality:</strong> {info.wabaQuality || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Verified Name:</strong> {info.verifiedName || "—"}
              </Typography>
            </Box>

            {/* editable credentials */}
            <Grid container spacing={2}>
              {fields.map(({ key, label }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    label={label}
                    value={formValues[key]}
                    onChange={handleChange(key)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
                gap: 1,
              }}
            >
              <LoadingButton
                variant="contained"
                size="small"
                loading={saving}
                onClick={handleSubmit}
                disabled={!selectedCustomerId}
              >
                Save changes
              </LoadingButton>
            </Box>
          </Paper>
        )}
      </Container>
    </Container>
  );
};

export default ManageAccounts;
